import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

export const REL_META = {
  CLIENT: { label: "Client", plural: "Clients", cls: "border-turq/60 text-turq" },
  COLLEAGUE: { label: "Colleague", plural: "Colleagues", cls: "border-silver/60 text-fg" },
  COLLABORATOR: { label: "Collaborator", plural: "Collaborators", cls: "border-dashed border-turq/60 text-fg" },
  EMPLOYER: { label: "Employer", plural: "Employers", cls: "border-gold/60 text-gold" },
};

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export const Avatar = ({ t, size = "h-14 w-14", text = "text-xl" }) =>
  t.photo ? (
    <img src={imgUrl(t.photo)} alt={`Portrait of ${t.author_name}`} loading="lazy" decoding="async" className={cn("shrink-0 rounded-full object-cover ring-1 ring-gold/50 ring-offset-2 ring-offset-canvas", size)} data-testid={`testimonial-photo-${t.id}`} />
  ) : (
    <span aria-hidden className={cn("grid shrink-0 place-items-center rounded-full border border-gold/60 bg-surface font-display italic tracking-wide text-gold", size, text)} data-testid={`testimonial-monogram-${t.id}`}>
      {initials(t.author_name)}
    </span>
  );

export const RelationshipBadge = ({ relationship, className }) => {
  const m = REL_META[relationship] || REL_META.CLIENT;
  return <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]", m.cls, className)} data-testid={`relationship-badge-${relationship}`}>{m.label}</span>;
};

export const QuoteMark = ({ className }) => (
  <span aria-hidden className={cn("mb-3 block h-[0.5em] select-none font-display leading-none text-gold", className)}>“</span>
);

export const Attribution = ({ t, showProject = true, size }) => {
  const sub = [t.author_role, t.company].filter(Boolean).join(", ");
  return (
    <figcaption className="flex items-center gap-4">
      <Avatar t={t} size={size} />
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-medium text-fg" data-testid={`testimonial-author-${t.id}`}>{t.author_name}</span>
          <RelationshipBadge relationship={t.relationship} />
        </p>
        {sub && <p className="mt-1 text-sm text-silver" data-testid={`testimonial-sub-${t.id}`}>{sub}</p>}
        <div className="mt-1 flex flex-wrap gap-x-4 text-xs">
          {showProject && t.project_slug && (
            <Link to={`/work/${t.project_slug}`} className="inline-flex items-center gap-1 text-turq hover:underline" data-testid={`testimonial-project-link-${t.id}`}>
              {t.project_name}<ArrowUpRight className="h-3 w-3" aria-hidden />
            </Link>
          )}
          {t.linkedin_url && (
            <a href={t.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-silver hover:text-turq" aria-label={`${t.author_name} on LinkedIn`} data-testid={`testimonial-linkedin-${t.id}`}>
              <Linkedin className="h-3 w-3" aria-hidden />LinkedIn
            </a>
          )}
        </div>
      </div>
    </figcaption>
  );
};
